/**
 * The setup forms, as one worksheet.
 *
 * THE BUG THIS FIXES FIRST. Every input in these editors was filled with
 * `surface.secondary` and given no border. `surface.secondary` is ALSO the page
 * background on the analysis screen — `#F5F5F7` in light, pure `#000000` in
 * dark — so the fields were invisible in both themes. A founder saw a label, a
 * hint, and a gap between them. They were not empty-looking; they were absent.
 *
 * So an input here is defined by contrast with the surface it sits on, never by
 * a fill that happens to differ from one particular page: the CARD is
 * `surface.primary`, the input is `surface.secondary` INSIDE that card, and a
 * hairline border carries the shape when the two are close. Focus adds weight
 * and colour to the same border rather than introducing a new element.
 *
 * The rest is grouping. These forms ask for a dozen numbers across three
 * concerns, and a flat column of labels gives a founder no idea which answers
 * belong together or which they may skip. Cards carry the concern, an "Optional"
 * chip carries the permission, and a range's three boxes get one row of column
 * headers so `low / base / high` survives being typed over — a placeholder that
 * disappears the moment it is answered is not a label.
 */

import React, { useState } from 'react';
import { Text, TextInput, View, type KeyboardTypeOptions } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';

type Colors = ReturnType<typeof useThemeColors>;

/** Every input in these forms. One height, one radius, one focus treatment. */
const FIELD_HEIGHT = 46;
const FIELD_RADIUS = 12;

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------

/**
 * One concern, carded.
 *
 * `tone="muted"` is for the optional block — present, clearly secondary, and
 * not competing with the answers that actually unblock the analysis.
 */
export function FormSection({
  title,
  description,
  tone = 'default',
  children,
}: {
  title: string;
  description?: string;
  tone?: 'default' | 'muted';
  children: React.ReactNode;
}) {
  const colors = useThemeColors();
  const muted = tone === 'muted';

  return (
    <View
      className="rounded-3xl p-4 gap-4"
      style={{
        backgroundColor: muted ? 'transparent' : colors.surface.primary,
        borderWidth: 1,
        borderColor: muted ? colors.border.light : colors.border.light,
        borderStyle: muted ? 'dashed' : 'solid',
        overflow: 'hidden',
      }}
    >
      <View className="gap-1">
        <Text className="text-[15px] font-bold" style={{ color: colors.text.primary }}>
          {title}
        </Text>
        {description && (
          <Text className="text-[12px] leading-4" style={{ color: colors.text.secondary }}>
            {description}
          </Text>
        )}
      </View>
      {children}
    </View>
  );
}

/** The heading over a form, with what it is for. */
export function FormHeader({ title, description }: { title: string; description: string }) {
  const colors = useThemeColors();
  return (
    <View className="gap-1.5">
      <Text className="text-[20px] font-bold" style={{ color: colors.text.primary }}>
        {title}
      </Text>
      <Text className="text-[13px] leading-5" style={{ color: colors.text.secondary }}>
        {description}
      </Text>
    </View>
  );
}

/** Permission, not a parenthetical glued onto a label. */
export function OptionalChip() {
  const colors = useThemeColors();
  return (
    <View
      className="rounded-full px-2 py-0.5 shrink-0"
      style={{ backgroundColor: colors.surface.secondary }}
    >
      <Text className="text-[10px] font-semibold uppercase" style={{ color: colors.text.tertiary }}>
        Optional
      </Text>
    </View>
  );
}

function FieldLabel({
  label,
  hint,
  optional,
}: {
  label: string;
  hint?: string;
  optional?: boolean;
}) {
  const colors = useThemeColors();
  return (
    <View className="gap-1">
      <View className="flex-row items-center gap-2">
        <Text
          numberOfLines={2}
          className="flex-1 text-[13px] font-semibold"
          style={{ color: colors.text.primary }}
        >
          {label}
        </Text>
        {optional && <OptionalChip />}
      </View>
      {hint && (
        <Text className="text-[11px] leading-4" style={{ color: colors.text.tertiary }}>
          {hint}
        </Text>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** The shared input chrome. Focus moves the border it already has. */
function inputStyle(colors: Colors, focused: boolean, invalid: boolean, multiline: boolean) {
  return {
    minHeight: multiline ? 96 : FIELD_HEIGHT,
    borderRadius: FIELD_RADIUS,
    paddingHorizontal: 12,
    paddingVertical: multiline ? 10 : 0,
    textAlignVertical: multiline ? ('top' as const) : ('center' as const),
    backgroundColor: colors.surface.secondary,
    color: colors.text.primary,
    borderWidth: focused || invalid ? 1.5 : 1,
    borderColor: invalid
      ? colors.error
      : focused
      ? colors.primary[600]
      : colors.border.light,
  };
}

export function TextField({
  label,
  hint,
  value,
  onChangeText,
  editable,
  optional,
  invalid,
  placeholder,
  keyboardType,
  autoCapitalize,
  multiline,
  accessibilityLabel,
}: {
  label: string;
  hint?: string;
  value: string;
  onChangeText: (text: string) => void;
  editable: boolean;
  optional?: boolean;
  invalid?: boolean;
  placeholder?: string;
  keyboardType?: KeyboardTypeOptions;
  autoCapitalize?: 'none' | 'characters' | 'sentences';
  multiline?: boolean;
  accessibilityLabel?: string;
}) {
  const colors = useThemeColors();
  const [focused, setFocused] = useState(false);

  return (
    <View className="gap-2">
      <FieldLabel label={label} hint={hint} optional={optional} />
      <TextInput
        accessibilityLabel={accessibilityLabel ?? label}
        value={value}
        onChangeText={onChangeText}
        editable={editable}
        placeholder={placeholder}
        placeholderTextColor={colors.text.tertiary}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        multiline={multiline}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={inputStyle(colors, focused, Boolean(invalid), Boolean(multiline))}
      />
    </View>
  );
}

/**
 * A bare input, for a form that supplies its own label and help.
 *
 * Exists so `StrategyContextEditor` — which wraps each field in candidate
 * chips, conflict copy and a publication toggle, and cannot use `TextField` —
 * still gets the same height, radius and focus treatment. Two forms sit on this
 * screen at once; inputs that behave differently between them read as two
 * different products.
 */
export function FormInput({
  value,
  onChangeText,
  accessibilityLabel,
  placeholder,
  multiline,
  invalid,
}: {
  value: string;
  onChangeText: (text: string) => void;
  accessibilityLabel: string;
  placeholder?: string;
  multiline?: boolean;
  invalid?: boolean;
}) {
  const colors = useThemeColors();
  const [focused, setFocused] = useState(false);

  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      accessibilityLabel={accessibilityLabel}
      placeholder={placeholder}
      placeholderTextColor={colors.text.tertiary}
      multiline={multiline}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={inputStyle(colors, focused, Boolean(invalid), Boolean(multiline))}
    />
  );
}

const BOUNDS = ['low', 'base', 'high'] as const;
export type RangeBound = (typeof BOUNDS)[number];

/**
 * A low/base/high triple.
 *
 * The column headers are the point. These were three boxes whose only labels
 * were placeholders, so the moment a founder typed the first number they lost
 * the map — and a range typed into the wrong order is not a validation error
 * the server can catch, it is a different assumption.
 */
export function RangeField({
  label,
  hint,
  values,
  onChange,
  editable,
  optional,
  invalid,
  unit,
}: {
  label: string;
  hint?: string;
  values: Record<RangeBound, string>;
  onChange: (bound: RangeBound, text: string) => void;
  editable: boolean;
  optional?: boolean;
  invalid?: boolean;
  /** Shown once, beside the label — not repeated into every box. */
  unit?: string;
}) {
  const colors = useThemeColors();
  const [focused, setFocused] = useState<RangeBound | null>(null);

  return (
    <View className="gap-2">
      <View className="flex-row items-start gap-2">
        <View className="flex-1">
          <FieldLabel label={label} hint={hint} optional={optional} />
        </View>
        {unit && (
          <View
            className="rounded-md px-2 py-0.5 shrink-0"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Text
              numberOfLines={1}
              className="text-[10px] font-semibold"
              style={{ color: colors.text.secondary }}
            >
              {unit}
            </Text>
          </View>
        )}
      </View>

      <View className="flex-row gap-2">
        {BOUNDS.map((bound) => (
          <View key={bound} className="flex-1 gap-1">
            <Text
              numberOfLines={1}
              className="text-[10px] font-semibold uppercase"
              style={{ color: colors.text.tertiary, letterSpacing: 0.4 }}
            >
              {bound}
            </Text>
            <TextInput
              accessibilityLabel={`${label} ${bound}`}
              value={values[bound]}
              onChangeText={(text) => onChange(bound, text)}
              editable={editable}
              keyboardType="decimal-pad"
              onFocus={() => setFocused(bound)}
              onBlur={() => setFocused(null)}
              style={inputStyle(colors, focused === bound, Boolean(invalid), false)}
            />
          </View>
        ))}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

/**
 * The save action, with what saving DOES stated before the button rather than
 * discovered after it.
 *
 * `blockedReason` is why the button is inert. A disabled button with no reason
 * is a dead end a founder cannot debug — they can see it is grey and cannot see
 * which of eleven fields is the problem.
 */
export function FormFooter({
  consequence,
  label,
  savingLabel,
  canSave,
  isSaving,
  blockedReason,
  onSave,
  accessibilityLabel,
}: {
  consequence: string;
  label: string;
  savingLabel: string;
  canSave: boolean;
  isSaving: boolean;
  blockedReason?: string | null;
  onSave: () => void;
  accessibilityLabel: string;
}) {
  const colors = useThemeColors();

  return (
    <View className="gap-2.5">
      <Text className="text-[11px] leading-4" style={{ color: colors.text.tertiary }}>
        {consequence}
      </Text>

      {!canSave && !isSaving && blockedReason && (
        <View className="flex-row items-start gap-2">
          <Text
            className="flex-1 text-[11px] leading-4"
            style={{ color: colors.amber.main }}
          >
            {blockedReason}
          </Text>
        </View>
      )}

      <View
        className="rounded-2xl overflow-hidden"
        style={{
          height: 50,
          backgroundColor: canSave ? colors.primary[600] : colors.surface.tertiary,
        }}
      >
        <AnimatedPressable
          onPress={onSave}
          disabled={!canSave}
          accessibilityRole="button"
          accessibilityState={{ disabled: !canSave }}
          accessibilityLabel={accessibilityLabel}
          className="items-center justify-center"
          style={{ flex: 1 }}
        >
          <Text
            numberOfLines={1}
            className="text-[15px] font-semibold"
            style={{ color: canSave ? colors.text.inverse : colors.text.tertiary }}
          >
            {isSaving ? savingLabel : label}
          </Text>
        </AnimatedPressable>
      </View>
    </View>
  );
}
